import { createApi } from '@reduxjs/toolkit/query/react';
import { adminBaseQuery } from '@store/baseQuery';
import { Speciality } from '@entities/specialities/api';
import { ConditionGroupLookup } from '@entities/condition-groups/api';
import { LookupStatus } from '@/utils/lookupStatus';

// Base types
export interface ConditionLookup {
  id: number;
  name: string;
  description?: string;
  status: LookupStatus;
  speciality: Speciality;
  group_ids: number[];
  created_at: string;
  updated_at?: string;
}

// Request types
export interface CreateConditionLookupRequest {
  name: string;
  description?: string;
  status: LookupStatus;
  speciality_id: number;
}

export interface UpdateConditionLookupRequest {
  name?: string;
  description?: string;
  status?: LookupStatus;
  speciality_id?: number;
}

export interface AssignGroupsToConditionRequest {
  group_ids: number[];
}

export interface PaginatedResponse<T> {
  current_page: number;
  data: T[];
  first_page_url: string;
  from: number;
  last_page: number;
  last_page_url: string;
  links: Array<{
    url: string | null;
    label: string;
    active: boolean;
  }>;
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number;
  total: number;
}

// Query parameter types
export interface ConditionLookupsQueryParams {
  page?: number;
  per_page?: number;
  search?: string;
  status?: LookupStatus | 'all';
  speciality_id?: number;
}

// API
export const conditionLookupsApi = createApi({
  reducerPath: "conditionLookupsApi",
  baseQuery: adminBaseQuery,
  tagTypes: ["ConditionLookup"],
  endpoints: (builder) => ({
    // Get all condition lookups with pagination
    getConditionLookups: builder.query<PaginatedResponse<ConditionLookup>, ConditionLookupsQueryParams | void>({
      query: (params) => ({
        url: "/condition-lookups",
        params: params || {},
      }),
      providesTags: ["ConditionLookup"],
    }),

    // Get a specific condition lookup
    getConditionLookup: builder.query<ConditionLookup, number>({
      query: (id) => `/condition-lookups/${id}`,
      providesTags: (result, error, id) => [
        { type: "ConditionLookup", id },
      ],
    }),

    // Create a new condition lookup
    createConditionLookup: builder.mutation<
      ConditionLookup,
      CreateConditionLookupRequest
    >({
      query: (data) => ({
        url: "/condition-lookups",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["ConditionLookup"],
    }),

    // Update a specific condition lookup
    updateConditionLookup: builder.mutation<
      ConditionLookup,
      { id: number; data: UpdateConditionLookupRequest }
    >({
      query: ({ id, data }) => ({
        url: `/condition-lookups/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "ConditionLookup", id },
        "ConditionLookup",
      ],
    }),

    // Delete a specific condition lookup
    deleteConditionLookup: builder.mutation<
      { condition_lookup_id: number },
      number
    >({
      query: (id) => ({
        url: `/condition-lookups/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ConditionLookup"],
    }),

    // Get groups assigned to a condition
    getConditionGroups: builder.query<ConditionGroupLookup[], number>({
      query: (id) => `/condition-lookups/${id}/groups`,
      providesTags: (result, error, id) => [
        { type: "ConditionLookup", id: `${id}-groups` },
      ],
    }),

    // Bulk change status
    bulkChangeStatus: builder.mutation<
      { message: string; updated_count: number; failed_count: number; updated_ids: number[] },
      { ids: number[]; status: LookupStatus }
    >({
      query: (data) => ({
        url: "/condition-lookups/bulk-status",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["ConditionLookup"],
    }),

    // Assign groups to a condition
    assignGroupsToCondition: builder.mutation<
      ConditionGroupLookup[],
      { id: number; data: AssignGroupsToConditionRequest }
    >({
      query: ({ id, data }) => ({
        url: `/condition-lookups/${id}/groups`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "ConditionLookup", id: `${id}-groups` },
        { type: "ConditionLookup", id },
        "ConditionLookup",
      ],
    }),
  }),
});

export const {
  useGetConditionLookupsQuery,
  useGetConditionLookupQuery,
  useCreateConditionLookupMutation,
  useUpdateConditionLookupMutation,
  useDeleteConditionLookupMutation,
  useGetConditionGroupsQuery,
  useAssignGroupsToConditionMutation,
  useBulkChangeStatusMutation,
} = conditionLookupsApi; 